import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
    Container,
    Card,
    Table,
    Button,
    Row,
    Col,
    Spinner,
    Form,
    InputGroup
} from "react-bootstrap";
import {
    FaClock,
    FaBell,
    FaFileContract,
    FaStethoscope,
    FaIdCard,
    FaClipboardCheck,
    FaCheckCircle,
    FaSearch,
    FaFilter
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";



const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

function AlertasVencimientos({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [filtro, setFiltro] = useState("todos");
    const [busqueda, setBusqueda] = useState("");
    const [coleccionEmail, setColeccionEmail] = useState("mailTurbo");
    const [resumen, setResumen] = useState({
        total: 0,
        contratosCriticos: 0,
        emoCriticos: 0,
        licenciaCriticos: 0,
        levantamientoCriticos: 0,
        proximos: 0
    });

    const ensureAuthenticated = () => {
        if (auth.currentUser) return true;
        Swal.fire("Sesión requerida", "Solo usuarios autenticados pueden usar alertas de vencimientos.", "warning");
        return false;
    };

    useEffect(() => {
        cargarAlertas();
        // Leer coleccionEmail desde el documento de empresa
        const ruc = usuario?.ruc || usuario?.rucEmpresa;
        if (ruc) {
            getDoc(doc(db, "company", ruc)).then(snap => {
                if (snap.exists()) {
                    const data = snap.data();
                    if (data.coleccionEmail && data.coleccionEmail.trim() !== "") {
                        setColeccionEmail(data.coleccionEmail.trim());
                    }
                }
            }).catch(() => {});
        }
    }, [usuario]);

    const cargarAlertas = async () => {
        if (!ensureAuthenticated()) return;
        setLoading(true);
        try {
            const ruc = usuario.ruc || usuario.rucEmpresa;
            const toSafeText = (value) => String(value ?? "").trim();

            // 1. Obtener empleados activos
            const usersRef = collection(db, "users");
            const qUsers = query(
                usersRef,
                where("rucEmpresa", "==", ruc),
                where("activo", "==", true)
            );
            const [usersSnap, empleadosRucEmpresaSnap, empleadosRucSnap] = await Promise.all([
                getDocs(qUsers),
                getDocs(
                    query(
                        collection(db, "pl_empleados"),
                        where("ruc_empresa", "==", ruc),
                        where("activo", "==", true)
                    )
                ),
                getDocs(
                    query(
                        collection(db, "pl_empleados"),
                        where("ruc", "==", ruc),
                        where("activo", "==", true)
                    )
                )
            ]);

            const empleadosByDni = new Map();
            [...empleadosRucEmpresaSnap.docs, ...empleadosRucSnap.docs].forEach((docRef) => {
                const data = docRef.data() || {};
                const dni = toSafeText(data.dni || docRef.id);
                if (!dni) return;
                empleadosByDni.set(dni, data);
            });

            const todasAlertas = [];
            let contratosCriticos = 0;
            let emoCriticos = 0;
            let licenciaCriticos = 0;
            let levantamientoCriticos = 0;
            let proximos = 0;

            const hoy = new Date();
            const SPANISH_MONTH_MAP = {
                enero: "01",
                febrero: "02",
                marzo: "03",
                abril: "04",
                mayo: "05",
                junio: "06",
                julio: "07",
                agosto: "08",
                setiembre: "09",
                septiembre: "09",
                octubre: "10",
                noviembre: "11",
                diciembre: "12"
            };
            const normalizeToIsoDate = (raw) => {
                if (!raw) return "";
                const source = toSafeText(raw);
                if (!source) return "";

                const isoMatch = source.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
                if (isoMatch) {
                    const [, y, m, d] = isoMatch;
                    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                }

                const latinMatch = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
                if (latinMatch) {
                    const [, d, m, y] = latinMatch;
                    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                }

                const lower = source.toLowerCase();
                const humanMonthMatch = lower.match(/(\d{1,2})\s*(?:de)?\s*([a-záéíóúñ]+)\s*(?:de)?\s*(\d{4})/i);
                if (humanMonthMatch) {
                    const day = String(humanMonthMatch[1]).padStart(2, "0");
                    const monthName = humanMonthMatch[2]
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "");
                    const month = SPANISH_MONTH_MAP[monthName];
                    const year = humanMonthMatch[3];
                    if (month) return `${year}-${month}-${day}`;
                }

                return "";
            };
            const parseLocalDate = (str) => {
                const iso = normalizeToIsoDate(str);
                if (!iso) return null;
                const [y, m, d] = iso.split("-").map(Number);
                return new Date(y, m - 1, d);
            };

            usersSnap.docs.forEach((doc) => {
                const emp = doc.data();
                const dniEmpleado = toSafeText(emp.dni || doc.id);
                const fichaEmpleado = empleadosByDni.get(dniEmpleado) || {};
                const nombreCompleto = `${emp.name || ""} ${emp.apellidos || ""}`.trim() || emp.nombre || "Empleado Desconocido";
                const pushAlerta = (tipo, rawDate, counterSetter) => {
                    if (!rawDate) return;
                    const venc = parseLocalDate(rawDate);
                    if (!venc || Number.isNaN(venc.getTime())) return;
                    const diffTime = venc - hoy;
                    const diasRestantes = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    if (diasRestantes > 30) return;

                    let color = "success";
                    let estado = "normal";
                    if (diasRestantes < 0) {
                        color = "danger";
                        estado = "vencido";
                        counterSetter();
                    } else if (diasRestantes <= 15) {
                        color = "danger";
                        estado = "critico";
                        counterSetter();
                    } else {
                        color = "warning";
                        estado = "proximo";
                        proximos++;
                    }

                    todasAlertas.push({
                        id: `${doc.id}-${tipo.toLowerCase().replace(/\s+/g, "-")}`,
                        dni: emp.dni,
                        email: emp.email || "",
                        supervisor: emp.supervisor || "",
                        recursosHumanos: emp.recursosHumanos || "",
                        empleado: nombreCompleto,
                        tipo,
                        vencimiento: venc,
                        diasRestantes,
                        color,
                        estado,
                        area: emp.cecoNombre || "General"
                    });
                };

                // Analizar Contrato
                const contratoRaw =
                    fichaEmpleado.fecha_fin_contrato ||
                    fichaEmpleado.contratoFechaFin ||
                    "";
                pushAlerta("Contrato", contratoRaw, () => { contratosCriticos++; });

                // Analizar EMO
                const emoRaw =
                    fichaEmpleado.fecha_EMO_fin ||
                    fichaEmpleado.vencimientoEMO ||
                    "";
                pushAlerta("EMO", emoRaw, () => { emoCriticos++; });

                // Analizar Brevete
                const licenciaRaw =
                    fichaEmpleado.fecha_brevete_fin ||
                    fichaEmpleado.vencimientoLicenciaConducir ||
                    fichaEmpleado.licenciaConducirVencimiento ||
                    fichaEmpleado.vencimiento_licencia_conducir ||
                    "";
                pushAlerta("Brevete", licenciaRaw, () => { licenciaCriticos++; });

                // Analizar Levantamiento Observaciones EMO
                const observacionRaw =
                    fichaEmpleado.fecha_OEMO_fin ||
                    fichaEmpleado.vencimientoLevantamientoObservacionMedico ||
                    fichaEmpleado.vencimientoLevantamientoObservacionMedica ||
                    fichaEmpleado.levantamientoObservacionMedicoVencimiento ||
                    fichaEmpleado.vencimiento_levantamiento_observacion_medico ||
                    "";
                pushAlerta("Levantamiento Observaciones EMO", observacionRaw, () => { levantamientoCriticos++; });
            });

            // Ordenar por urgencia (vencidos primero)
            todasAlertas.sort((a, b) => a.diasRestantes - b.diasRestantes);

            setAlertas(todasAlertas);
            setResumen({
                total: todasAlertas.length,
                contratosCriticos,
                emoCriticos,
                licenciaCriticos,
                levantamientoCriticos,
                proximos
            });

        } catch (error) {
            console.error("Error al cargar alertas:", error);
            Swal.fire("Error", "No se pudieron cargar las alertas de vencimientos", "error");
        } finally {
            setLoading(false);
        }
    };

    const filtrarAlertas = () => {
        return alertas.filter(alerta => {
            const coincideTipo = filtro === "todos" ||
                (filtro === "contratos" && alerta.tipo === "Contrato") ||
                (filtro === "emo" && alerta.tipo === "EMO") ||
                (filtro === "licencia" && alerta.tipo === "Brevete") ||
                (filtro === "levantamiento" && alerta.tipo === "Levantamiento Observaciones EMO");
            const coincideBusqueda = alerta.empleado.toLowerCase().includes(busqueda.toLowerCase()) ||
                alerta.dni.includes(busqueda);
            return coincideTipo && coincideBusqueda;
        });
    };

    const alertasFiltradas = filtrarAlertas();

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
                    {/* Header Premium con degradado azul - igual que Gestión de Usuarios */}
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            borderBottom: "3px solid #f59e0b"
                        }}
                    >
                        <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
                            <BotonManualHeader />
                        </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaBell className="me-3" />
                                Alertas de Vencimientos
                            </h2>
                            <p className="mb-0 opacity-75">
                                Control de contratos, EMO, brevetes y levantamientos de observaciones EMO
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>

                        {/* KPIs modernos */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaFileContract size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.contratosCriticos}</h4>
                                        <p className="mb-0 small opacity-90">Contratos Críticos</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaStethoscope size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.emoCriticos}</h4>
                                        <p className="mb-0 small opacity-90">EMOs Críticos</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaClock size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.proximos}</h4>
                                        <p className="mb-0 small opacity-90">Próximos (30 días)</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaBell size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.total}</h4>
                                        <p className="mb-0 small opacity-90">Total Alertas</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Filtros */}
                        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                            <Card.Body className="p-3">
                                <Row className="align-items-center g-2">
                                    <Col md={5}>
                                        <InputGroup>
                                            <InputGroup.Text className="bg-white border-end-0" style={{ borderRadius: "10px 0 0 10px" }}>
                                                <FaSearch className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                className="border-start-0"
                                                placeholder="Buscar por nombre o DNI..."
                                                value={busqueda}
                                                onChange={(e) => setBusqueda(e.target.value)}
                                                style={{ borderRadius: "0 10px 10px 0" }}
                                            />
                                        </InputGroup>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-flex align-items-center gap-2">
                                            <FaFilter className="text-muted flex-shrink-0" />
                                            <Form.Select
                                                value={filtro}
                                                onChange={(e) => setFiltro(e.target.value)}
                                                style={{ borderRadius: "10px" }}
                                            >
                                                <option value="todos">Todos los tipos</option>
                                                <option value="contratos">Solo Contratos</option>
                                                <option value="emo">Solo EMOs</option>
                                                <option value="licencia">Solo Brevete</option>
                                                <option value="levantamiento">Solo Levantamiento Observaciones EMO</option>
                                            </Form.Select>
                                        </div>
                                    </Col>
                                    <Col md={3} className="d-flex justify-content-end">
                                        <Button
                                            onClick={cargarAlertas}
                                            disabled={loading}
                                            className="d-flex align-items-center gap-2 py-2 px-4"
                                            style={{
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                border: "none",
                                                boxShadow: "0 4px 8px rgba(59, 130, 246, 0.3)"
                                            }}
                                        >
                                            {loading ? <Spinner animation="border" size="sm" /> : null}
                                            Actualizar
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                            <Card.Body className="py-3">
                                <small className="text-muted">
                                    El envío de notificaciones al trabajador y resúmenes por correo a Supervisor/RRHH se realiza automáticamente por scheduler diario.
                                </small>
                            </Card.Body>
                        </Card>

                        {/* Tabla */}
                        <Card className="shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
                            <Card.Body className="p-0">
                                <Table responsive hover className="mb-0 align-middle">
                                    <thead>
                                        <tr style={{
                                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                            color: "white",
                                            fontSize: "0.82rem",
                                            fontWeight: "600",
                                            letterSpacing: "0.05em"
                                        }}>
                                            <th className="py-3 px-3 border-0">EMPLEADO</th>
                                            <th className="py-3 px-3 border-0">TIPO ALERTA</th>
                                            <th className="py-3 px-3 border-0">VENCIMIENTO</th>
                                            <th className="py-3 px-3 border-0 text-center">ESTADO</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ fontSize: "0.9rem" }}>
                                        {alertasFiltradas.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="text-center py-5 text-muted">
                                                    <FaCheckCircle size={40} className="text-success mb-3 d-block mx-auto" />
                                                    No se encontraron alertas críticas con los filtros actuales
                                                </td>
                                            </tr>
                                        ) : (
                                            alertasFiltradas.map((alerta) => (
                                                <tr key={alerta.id}>
                                                    <td className="py-3 px-3">
                                                        <div className="fw-bold">{alerta.empleado}</div>
                                                        <small className="text-muted">{alerta.dni} | {alerta.area}</small>
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <span
                                                            className="px-3 py-1 rounded-pill text-white small fw-semibold"
                                                            style={{
                                                                background: alerta.tipo === "Contrato"
                                                                    ? "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
                                                                    : alerta.tipo === "EMO"
                                                                        ? "linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)"
                                                                        : alerta.tipo === "Brevete"
                                                                            ? "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)"
                                                                            : "linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)",
                                                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                                            }}
                                                        >
                                                            {alerta.tipo === "Contrato" ? <FaFileContract className="me-1" /> :
                                                                alerta.tipo === "EMO" ? <FaStethoscope className="me-1" /> :
                                                                    alerta.tipo === "Brevete" ? <FaIdCard className="me-1" /> :
                                                                        <FaClipboardCheck className="me-1" />}
                                                            {alerta.tipo}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <div className="fw-bold">{alerta.vencimiento.toLocaleDateString()}</div>
                                                        <small className={alerta.diasRestantes < 0 ? "text-danger fw-bold" : "text-muted"}>
                                                            {alerta.diasRestantes < 0
                                                                ? `Vencido hace ${Math.abs(alerta.diasRestantes)} días`
                                                                : `Vence en ${alerta.diasRestantes} días`}
                                                        </small>
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        <span
                                                            className="px-3 py-1 rounded-pill text-white small fw-semibold"
                                                            style={{
                                                                background: alerta.color === "danger"
                                                                    ? "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
                                                                    : alerta.color === "warning"
                                                                        ? "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                                                                        : "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                                                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                                            }}
                                                        >
                                                            {alerta.estado === "vencido" ? "Vencido" : alerta.estado === "critico" ? "Crítico" : "Próximo"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default AlertasVencimientos;
